import React from "react";
import { useTranslation } from "react-i18next";
import { convertTime, truncateUrl } from "../../../lib/globalUtils";
import { getWhatsAppTestPayload } from "../../../lib/whatsappTestLink";
import { getFinitlessChatSiteUrl } from "../../../lib/linkInBio";
import { getBrandIcon } from "@finitless/shared/lib";
import finitlessIsotype from "../../../assets/finitless-isotype.png";
import { ExternalLink, Code, Settings } from "lucide-react";
import { Switch, ContextHelp } from "../../ui";

const AgentCard = ({
  agent,
  onClick,
  onWidgetClick,
  onTestWhatsApp,
  onToggleEnabled,
  isTogglePending = false,
}) => {
  const { t } = useTranslation("aiAgents");
  const isEnabled = !!agent?.enabled;
  const isConfigured = !!agent?.configured;
  const misconfiguredReason = agent?.misconfigured_reason;
  const isWhatsApp = agent?.provider === "whatsapp";
  const whatsappTestPayload = isWhatsApp ? getWhatsAppTestPayload(agent) : null;

  // Determine status: inactive, active (configured), or warning (enabled but not configured)
  const getStatusInfo = () => {
    if (!isEnabled) {
      return {
        label: t("status.inactive"),
        colorClass: "border-red-500/30 bg-red-500/20 text-red-400",
        helpNote: t("help.status.inactive"),
      };
    }
    if (isConfigured) {
      return {
        label: t("status.active"),
        colorClass: "border-green-500/30 bg-green-500/20 text-green-400",
        helpNote: t("help.status.activeConfigured"),
      };
    }
    // Enabled but not configured
    let reasonText = null;
    if (misconfiguredReason) {
      const reasonKey = `whatsappOAuth.errors.${misconfiguredReason}`;
      const translatedReason = t(reasonKey);
      reasonText =
        translatedReason !== reasonKey ? translatedReason : misconfiguredReason;
    }
    return {
      label: t("status.active"),
      colorClass: "border-yellow-500/30 bg-yellow-500/20 text-yellow-400",
      helpNote: reasonText || t("help.status.notConfigured"),
      helpNoteClass: "text-red-400",
    };
  };

  const statusInfo = getStatusInfo();

  const handleEnabledCheckedChange = (nextChecked) => {
    if (isTogglePending) return;
    if (!agent) return;
    if (typeof onToggleEnabled !== "function") return;
    onToggleEnabled(agent, nextChecked);
  };

  const getProviderIcon = (provider) => {
    switch (provider) {
      case "whatsapp":
        return (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-500">
            <svg
              className="h-6 w-6 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
            </svg>
          </div>
        );
      case "finitless":
        return (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-gray-700 bg-gray-800">
            {getBrandIcon(finitlessIsotype) && (
              <img
                src={getBrandIcon(finitlessIsotype)}
                alt={t("card.logoAlt")}
                className="h-8 w-8 object-contain"
              />
            )}
          </div>
        );
      default:
        return (
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gray-500">
            <svg
              className="h-6 w-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
        );
    }
  };

  const getProviderName = (provider) => {
    switch (provider) {
      case "whatsapp":
        return t("card.providers.whatsapp");
      case "phone":
        return t("card.providers.phone");
      case "web":
        return t("card.providers.web");
      case "finitless":
        return t("card.providers.default");
      default:
        return t("card.providers.default");
    }
  };

  const getIdentifier = (agent) => {
    if (agent.identifier) {
      return agent.identifier;
    }

    if (agent.provider === "whatsapp" || agent.provider === "phone") {
      const countryCode = agent.provider_settings?.country_code || "1";
      const phoneNumber = agent.provider_settings?.phone_number || "0000000000";
      return `+${countryCode}-${phoneNumber}`;
    }

    if (agent.provider === "web" && agent.provider_settings?.website_url) {
      return agent.provider_settings?.website_url;
    }

    return agent.provider_external_id || t("card.unknown");
  };

  const getDomainInfo = (agent) => {
    if (
      agent.provider === "finitless" &&
      agent.provider_type === "chat" &&
      agent.site
    ) {
      const url = getFinitlessChatSiteUrl(agent.site);

      return {
        url,
        text: truncateUrl(url),
      };
    }

    return {
      text: getIdentifier(agent),
    };
  };

  return (
    <div
      className="flex h-full cursor-pointer flex-col rounded-lg border border-gray-700 bg-gray-800 p-6 transition-colors hover:border-gray-600"
      onClick={() => onClick(agent)}
    >
      <div className="mb-4 flex min-w-0 items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center space-x-3">
          <div className="flex-shrink-0">{getProviderIcon(agent.provider)}</div>
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
              <h3 className="min-w-0 truncate text-lg font-semibold text-white">
                {getProviderName(agent.provider)}
              </h3>
              {agent?.id != null && String(agent.id).trim() !== "" ? (
                <span
                  data-testid="agent-card-id"
                  className="inline-flex max-w-full shrink-0 items-center rounded-md border border-gray-600/90 bg-gray-950/50 px-2 py-0.5 font-mono text-[11px] leading-none font-semibold tracking-wide text-gray-400 tabular-nums"
                  title={t("card.agentIdTooltip", { id: String(agent.id) })}
                >
                  {t("card.agentIdBadge", { id: String(agent.id) })}
                </span>
              ) : null}
            </div>
            {(() => {
              const domainInfo = getDomainInfo(agent);
              if (domainInfo?.url) {
                if (!isEnabled) {
                  return (
                    <div
                      className="flex min-w-0 cursor-not-allowed items-center gap-1 text-sm text-gray-500"
                      title={t("sites.tooltips.agentDisabledLink")}
                    >
                      <span className="min-w-0 truncate">
                        {domainInfo.text}
                      </span>
                      <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-50" />
                    </div>
                  );
                }

                return (
                  <a
                    href={domainInfo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="group flex min-w-0 items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                    title={domainInfo.url}
                  >
                    <span className="min-w-0 truncate">{domainInfo.text}</span>
                    <ExternalLink className="h-3 w-3 flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100" />
                  </a>
                );
              }
              return (
                <p className="truncate text-sm text-gray-400">
                  {domainInfo.text}
                </p>
              );
            })()}
          </div>
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <Switch
            checked={isEnabled}
            onCheckedChange={handleEnabledCheckedChange}
            disabled={isTogglePending}
            aria-label={
              isEnabled
                ? t("card.actions.disableAgent")
                : t("card.actions.enableAgent")
            }
            onClick={(e) => e.stopPropagation()}
          />

          <span
            className={`rounded-full border px-2 py-1 text-xs font-medium ${statusInfo.colorClass}`}
          >
            {statusInfo.label}
          </span>
          <ContextHelp
            className="h-3.5 w-3.5"
            ariaLabel={t("help.status.ariaLabel")}
            whatIs={t("help.status.whatIs")}
            note={statusInfo.helpNote}
            noteClass={statusInfo.helpNoteClass}
          />
        </div>
      </div>

      <div className="mb-6 space-y-3">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-sm text-gray-400">
            {t("metrics.successRate")}
            <ContextHelp
              ariaLabel={t("help.metrics.successRate.ariaLabel")}
              whatIs={t("help.metrics.successRate.whatIs")}
              formula={t("help.metrics.successRate.formula")}
              period={t("help.metrics.successRate.period")}
            />
          </span>
          <span className="text-lg font-medium text-green-400">
            {agent.metrics?.success_rate || 0}%
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-sm text-gray-400">
            {t("metrics.avgResponseTime")}
            <ContextHelp
              ariaLabel={t("help.metrics.avgResponseTime.ariaLabel")}
              whatIs={t("help.metrics.avgResponseTime.whatIs")}
              period={t("help.metrics.avgResponseTime.period")}
            />
          </span>
          <span className="text-sm font-medium text-white">
            {convertTime(
              agent.metrics?.average_response_time_ms || 0,
              "ms",
              "s",
              { decimals: 1 },
            )}
          </span>
        </div>
      </div>

      <div className="mt-auto flex flex-wrap gap-2">
        <button
          type="button"
          className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-gray-700 px-3 py-2 text-sm font-medium whitespace-nowrap text-white transition-colors hover:bg-gray-600"
          onClick={(e) => {
            e.stopPropagation();
            onClick(agent);
          }}
        >
          <Settings className="h-4 w-4" />
          {t("card.actions.configure")}
        </button>
        {isWhatsApp && (
          <button
            type="button"
            disabled={!whatsappTestPayload?.isReady}
            title={
              !whatsappTestPayload?.isReady
                ? t("card.actions.testWhatsAppMissingData")
                : undefined
            }
            className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-green-600 px-3 py-2 text-sm font-medium whitespace-nowrap text-white transition-colors hover:bg-green-500 disabled:cursor-not-allowed disabled:bg-gray-600 disabled:text-gray-300"
            onClick={(e) => {
              e.stopPropagation();
              if (!whatsappTestPayload?.isReady) {
                return;
              }
              onTestWhatsApp?.(agent);
            }}
          >
            <ExternalLink className="h-4 w-4" />
            {t("card.actions.testWhatsAppAgent")}
          </button>
        )}
        {agent.provider === "finitless" &&
          agent.provider_type === "chat" &&
          agent.site && (
            <button
              type="button"
              className="flex flex-1 items-center justify-center gap-1.5 rounded-md bg-purple-600 px-3 py-2 text-sm font-medium whitespace-nowrap text-white transition-colors hover:bg-purple-500"
              onClick={(e) => {
                e.stopPropagation();
                onWidgetClick?.(agent);
              }}
            >
              <Code className="h-4 w-4" />
              {t("card.actions.widget")}
            </button>
          )}
      </div>
    </div>
  );
};

export default AgentCard;
